import {useEffect, useRef, useState} from "react";
import {Box, Button, Chip, CircularProgress, Drawer, IconButton, InputAdornment, InputBase, Paper, Skeleton, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import axios from "axios";
import {useSearchParams} from "react-router";
import moment from "moment-timezone";
import {PrimaryColor} from "../../component/theme/theme";
import SearchIcon from "@mui/icons-material/Search";
import {Cancel} from "@mui/icons-material";
import Divider from "@mui/material/Divider";
import {createTheme, ThemeProvider} from "@mui/material/styles";
import {tooltipStyleOverrides} from "../../GlobalStyle";
import ContactForm from "../../component/contact/ContactForm";
import {useAuth} from "../AuthProvider/AuthProvider";
import {useJobContext} from "../JobContextProvider/JobContextProvider";
import {isViewReadOnly, shouldSkipFetch} from "./dataManagementReadOnly";
import {TapeDetails} from "../TapeDetails/tapeDetails";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {FILES_RESTORE_TABLE_COLUMNS, TAPE_RESTORE_TABLE_COLUMNS} from "./restoreTableConfig";
import DataTable from "../../component/DataTable/DataTable";
import {fetchDeploymentType, fetchFileCountData, fetchFileData, fetchHydrateStatus, fetchTapeCountData, fetchTapeData, fetchTapeDataWithCount, requestHydration} from "../../utils/dashboardCalls";
import {Filters} from "../../component/Filters/Filters";
import {restoreFiltersStructure} from "../../component/Filters/filtersConfig";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {mediaFilterChipText, mediaFilterMatches, mediaFilterRequestOptions, resolveMediaFilter} from "../../component/widgets/MediaMetrics/mediaMetricsDataRetrieving";
import {MEDIA_FILTER_PARAM} from "../../component/widgets/MediaMetrics/mediaFilterNavigation";
import {AppSnackbar, ConfirmDialog, DateTimePickerField, HintText, StyledButtonContained, StyledButtonOutlined, ToggleButtonGroupField, useStyles} from "../../utils/uiUtils";
import {formatData} from "../../utils/utils";
import {Controller, useForm} from "react-hook-form";
import {addClickCallback, addCustomRenderer} from "../../component/DataTable/tableUtils";
import {TabbedWrapper} from "../../component/TabbedWrapper/TabbedWrapper";
import {OptionsDialog} from "../../component/OptionsDialog/OptionsDialog";
import {CheckoutSummaryDialog} from "../../component/CheckoutSummaryDialog/CheckoutSummaryDialog";
import {
    restoreOptionsConfig,
    destructionOptionsConfig,
    restoreCanProceed,
    destructionCanProceed,
    TAPE_SUMMARY_COLUMNS,
    FILE_SUMMARY_COLUMNS,
} from "./dataManagementConfig";
import {useHydration} from "./useHydration";

const bottomDrawerHeight = "5rem";

const dataManagementTheme = createTheme({
    palette: {
        primary: {main: "#FFC540"},
    },
    components: {
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    borderRadius: "8px", textTransform: "none", background: "transparent", color: "#FFFFFF",
                    "&.Mui-selected": {
                        backgroundColor: "#FFC540", color: "#3E3A3F",
                        "&:hover": {backgroundColor: "#FFC540", color: "#3E3A3F"},
                        "&.file-type": {border: "1px solid #FFC540", background: "rgba(255, 197, 64, 0.1)", color: "#FFC540"},
                    },
                    "&.display-mode": {fontWeight: 700, color: "#FFFFFF", height: 50, width: 140, fontSize: 12},
                    "&.file-type": {border: "1px solid #FFFFFF", width: "160px", height: "70px"},
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {root: {borderBottom: "1px solid #5F5F5F", "&:last-child td, &:last-child th": {border: 0}}},
        },
        // tooltipStyleOverrides is spread in because this theme's `components` replaces the
        // root theme's rather than merging with it, so the readable size is not inherited.
        MuiTooltip: {styleOverrides: {tooltip: {...tooltipStyleOverrides, "&.wideTooltip": {maxWidth: "60em"}}}},
    },
});

const PERTH_TZ = "Australia/Perth";
const POLL_INTERVAL_SECONDS = 5 * 60;

/**
 * CountdownDisplay — isolated component so its per-second state updates
 * never propagate to the parent DataManagement re-render.
 * When countdown reaches 0 it calls onExpired() exactly once.
 */
const CountdownDisplay = ({targetUTC, onExpired}) => {
    const [display, setDisplay] = useState("...");
    const firedRef = useRef(false);

    useEffect(() => {
        firedRef.current = false;
        const tick = () => {
            const secs = Math.max(0, moment(targetUTC).diff(moment(), 'seconds'));
            setDisplay(formatData(secs, 'countdown'));
            if (secs <= 0 && !firedRef.current) {
                firedRef.current = true;
                onExpired?.();
            }
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [targetUTC]);
    
    return <span>{display ?? "..."}</span>;
};

const TablePlaceholder = () => (
    <Box sx={{position: 'relative', width: '100%', height: 400}}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px'}}/>
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <CircularProgress color="primary"/>
        </Box>
    </Box>
);

const HYDRATION_PRESETS = [
    {label: "1 Day",   days: 1},
    {label: "2 Days",  days: 2},
    {label: "1 Week",  days: 7},
    {label: "2 Weeks", days: 14},
    {label: "Custom",  days: null},
];

export const DataManagement = () => {
    const {features, isFeatureAvailable, user: userDetails, isAdmin} = useAuth();
    const {selectedJobIds, selectedCid, setSelectorEnabled, allJobIds = [], jobGroupsPending = false} = useJobContext();

    /*
     * What this page lists when the header selector reads 'All Jobs'.
     *
     * Nothing ticked means the jobs the selector lists, for every reader — which is the substitution the
     * dashboard's tiles make, so that a tile counting 46 tapes opens a table listing 46 rather than a
     * wider answer than the figure gave.
     *
     * Not gated on `isAdmin`, which is what it used to be and what made this wrong. The reason to send ids
     * is not only that an admin has no cid to send: with a cid but no job id, ItemListFulfiller and
     * ItemCountFulfiller substitute the deployment's own configured `JobId` instead of reading the absence
     * as 'every job', so a customer at 'All Jobs' was listing one arbitrary job's tapes — the same wrong
     * scope the tiles had, which is why the two agreed and neither looked broken. See shouldSkipFetch.
     *
     * While that list is still on its way there is nothing to bound a request with, and firing one anyway
     * is exactly the unbounded fetch being avoided — hence the wait rather than a fetch and a correction.
     * Safe to wait on because the header resolves the pending flag on every path out of its request, the
     * nothing-to-fetch case included (see JobContextProvider).
     */
    const usesJobList = selectedJobIds.length === 0;
    const scopeJobIds = usesJobList ? allJobIds : selectedJobIds;
    const jobIdsKey = scopeJobIds.join(',');
    const waitingForJobList = usesJobList && jobGroupsPending;
    const nothingToList = shouldSkipFetch(scopeJobIds);
    // Admin uses this view to "view as a customer" via the Jobs selector — strictly read-only.
    // No mutating action may execute on the customer's behalf.
    const isReadOnly = isViewReadOnly(isAdmin);
    const classes = useStyles();
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    useEffect(() => {
        // After the initial page render, wait a moment before triggering the table to load.
        const timer = setTimeout(() => {
            setIsInitialLoad(false);
        }, 200);
        return () => clearTimeout(timer);
    }, []);

    // DataManagement consumes the job context selector — enable on mount, disable on unmount.
    useEffect(() => {
        setSelectorEnabled(true);
        return () => setSelectorEnabled(false);
    }, [setSelectorEnabled]);

    const hasDestruction = isFeatureAvailable("destruction");
    const tabsConfig = [
        {key: "restore", label: "Restore"},
        ...(hasDestruction ? [{key: "destruction", label: "Destruction"}] : []),
    ];

    // ─── Tab state ───────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState("restore");

    // ─── Data & display state ────────────────────────────────────────────────────
    const [listFilter, setListFilter] = useState("tapeList");
    const [columnsConfig, setColumnsConfig] = useState(TAPE_RESTORE_TABLE_COLUMNS["Standard tapes"]);
    const [selectedData, setSelectedData] = useState();
    const [openTapeDetails, setOpenTapeDetails] = useState(false);
    const [restoreData, setRestoreData] = useState([]);
    const [filesTypeAnchorEl, setFilesTypeAnchorEl] = useState(null);
    const [filesType, setFilesType] = useState('Standard files view');
    const [addedRows, setAddedRows] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const [filterData, setFilterData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [entriesCount, setEntriesCount] = useState(0);
    const [displayMode, setDisplayMode] = useState("All");
    const [snackbar, setSnackbar] = useState({open: false, message: "", severity: "info"});
    const openSnackbar = (message, severity = "info") => {
        const normalizedMessage = typeof message === 'string' ? message.trim() : message;
        if (!normalizedMessage) {
            return;
        }
        setSnackbar({open: true, message: normalizedMessage, severity});
    };
    const closeSnackbar = () => setSnackbar(s => ({...s, open: false}));

    /*
     * The tape list's filter, read off the URL rather than held in state.
     *
     * Set by clicking a Media Metrics tile on the dashboard, which navigates here with `?media=<key>`
     * naming the criteria that tile's number was counted with. The list then narrows to exactly what was
     * counted, so the figure the reader clicked and the rows they land on are two views of one query
     * rather than two independent guesses at it.
     *
     * In the query string for the same reasons the job page keeps it there: it survives a reload, it can be
     * pasted to a colleague, and arriving cold on the link shows the same rows as arriving by a click.
     *
     * Only the tape list honours it. A filter key names media criteria — a cartridge's status and the notes
     * against it — and the files list is a different endpoint over different rows, so a filter it cannot
     * apply is one it must not appear to have applied. The parameter is left in the URL while the reader
     * looks at files, so switching back to tapes returns them to the list they arrived on.
     *
     * An unrecognised key resolves to undefined and the list is simply unfiltered: a stale bookmark should
     * show the media in scope, not an error.
     */
    const [searchParams, setSearchParams] = useSearchParams();
    const mediaFilter = listFilter === 'tapeList' ? resolveMediaFilter(searchParams.get(MEDIA_FILTER_PARAM)) : undefined;
    const mediaFilterKey = mediaFilter?.key ?? '';
    // Both tape requests narrow by this. `{}` when there is no filter, which keeps every request
    // byte-identical to what it was before this feature existed, cache keys included.
    const filterOptions = mediaFilterRequestOptions(mediaFilter);

    /** Drops the filter, leaving the reader here with every tape in scope listed. */
    const clearMediaFilter = () => {
        const next = new URLSearchParams(searchParams);
        next.delete(MEDIA_FILTER_PARAM);
        // `replace` so Back goes to wherever the reader came from rather than to the filtered list they
        // have just dismissed.
        setSearchParams(next, {replace: true});
    };


    // ─── Hydration (extracted to custom hook) ────────────────────────────────────
    const {
        hydrationInfo,
        hasFiles,
        refreshHydrationStatusRef,
        hydrationPollTarget,
        setHydrationPollTarget,
        confirmOpen,
        setConfirmOpen,
        selectedPreset,
        setSelectedPreset,
        customDate,
        setCustomDate,
        minLocalDate,
        canRequestHydration,
        selectedDisplayLocal,
        displayInLocalTz,
        handleHydrationRequested,
    } = useHydration({ openSnackbar });

    // ─── Checkout flow state ─────────────────────────────────────────────────────
    const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
    const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
    const [contactFormOpen, setContactFormOpen] = useState(false);
    const [actionPreferences, setActionPreferences] = useState({});

    // ─── Search form ─────────────────────────────────────────────────────────────
    const {handleSubmit, formState: {errors}, setValue, control} = useForm({
        defaultValues: {'searchBar': ''},
        mode: "onBlur",
        reValidateMode: "onChange",
    });
    const submitSearchFn = (data) => setSearchQuery(data.searchBar);

    // ─── Data fetching ───────────────────────────────────────────────────────────
    const refreshFilesData = (search = "", filters = {}, signal) => {
        fetchFileCountData(search, filters, scopeJobIds, selectedCid, { signal })
            .then(_count => {
                if (signal?.aborted) return;
                setTotalPages(Math.ceil(_count / 10));
                setEntriesCount(_count);
                getFileData(1, search, filters, signal);
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                console.log("Error fetching file count", error);
            });
    };

    const getFileData = (pageNumber, search = "", filters = {}, signal) => {
        fetchFileData(pageNumber, search, filters, scopeJobIds, selectedCid, { signal })
            .then(resultsArray => {
                const enrichedResultsArray = resultsArray.map(file_item => ({
                    ...file_item,
                    item_id: file_item?.version_id ? `${file_item.file_id}_${file_item.version_id}` : file_item.file_id
                }));
                setRestoreData(enrichedResultsArray.slice(0, 10));
                mapAddedRows(enrichedResultsArray.slice(0, 10));
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                console.log("Error fetching file data", error);
                setRestoreData([]);
            });
    };

    /*
     * The listed rows re-tested against the filter in the browser, as a second line of defence.
     *
     * `man_item_list` gained the two Metadata phrase parameters recently, and an older SaaS ignores an
     * unrecognised parameter instead of rejecting it: the request succeeds with the filter silently dropped
     * and every tape in scope arrives. Left alone, the table would then list media directly beneath a chip
     * naming a narrower set. Re-testing each row makes the worst case a short page rather than a wrong one.
     *
     * It does not correct the total, which comes from man_item_count — that endpoint has had both
     * parameters all along, so a short page under a larger total is exactly the signal that the list
     * endpoint is behind.
     */
    const applyFilterToRows = (rows) => (mediaFilter ? rows.filter(row => mediaFilterMatches(mediaFilter, row)) : rows);

    const refreshTapesData = (search = "", signal) => {
        fetchTapeDataWithCount(1, search, scopeJobIds, selectedCid, { signal, ...filterOptions })
            .then(({rows: listedRows, count}) => {
                if (signal?.aborted) return;
                const rows = applyFilterToRows(listedRows);
                if (count !== null) {
                    setTotalPages(Math.ceil(count / 10));
                    setEntriesCount(count);
                    setRestoreData(rows.slice(0, 10));
                    mapAddedRows(rows.slice(0, 10));
                    return;
                }

                // If the list response doesn't include a total count, fall back to the count endpoint.
                const fallbackCount = rows.length;
                setRestoreData(rows.slice(0, 10));
                mapAddedRows(rows.slice(0, 10));
                fetchTapeCountData(search, scopeJobIds, selectedCid, { signal, ...filterOptions })
                    .then(_count => {
                        if (signal?.aborted) return;
                        setTotalPages(Math.ceil(_count / 10));
                        setEntriesCount(_count);
                    })
                    .catch((error) => {
                        if (axios.isCancel(error)) return;
                        console.log("Error fetching tape count fallback", error);
                        setTotalPages(Math.ceil(fallbackCount / 10));
                        setEntriesCount(fallbackCount);
                    });
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                console.log("Error fetching tape data", error);
                setRestoreData([]);
            });
    };

    const getTapeData = (pageNumber, search = "", signal) => {
        fetchTapeData(pageNumber, search, scopeJobIds, selectedCid, { signal, ...filterOptions })
            .then(resultsArray => {
                const rows = applyFilterToRows(resultsArray);
                setRestoreData(rows.slice(0, 10));
                mapAddedRows(rows.slice(0, 10));
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                console.log("Error fetching tape data", error);
                setRestoreData([]);
            });
    };

    const refreshTableData = (_listFilter, search = "", filters = {}, signal) => {
        if (_listFilter === 'tapeList') {
            setColumnsConfig(TAPE_RESTORE_TABLE_COLUMNS["Standard tapes"]);
            refreshTapesData(search, signal);
        } else if (_listFilter === 'itemList') {
            refreshFilesData(search, filters, signal);
            setColumnsConfig(FILES_RESTORE_TABLE_COLUMNS[filesType]);
        }
    };

    useEffect(() => {
        // Nothing to ask for yet (the first render's pause, or the selector's job list still coming), or
        // nothing that could be asked without listing every company's media. Either way the table is
        // emptied rather than left showing rows from the scope before this one.
        if (isInitialLoad || waitingForJobList || nothingToList) {
            setRestoreData([]);
            setEntriesCount(0);
            setTotalPages(1);
            return;
        }
        const controller = new AbortController();
        refreshTableData(listFilter, searchQuery, filterData, controller.signal);
        return () => {
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // mediaFilterKey rather than the descriptor: a new object every render would re-fetch on every
        // render, and the key is what actually changes when the reader clicks a tile or clears the chip.
    }, [isInitialLoad, waitingForJobList, nothingToList, features, listFilter, searchQuery, filterData, jobIdsKey, selectedCid, mediaFilterKey]);

    useEffect(() => {
        setCurrentPage(1);
    }, [listFilter, searchQuery, filterData, jobIdsKey, mediaFilterKey]);

    // ─── Row selection ───────────────────────────────────────────────────────────
    const handleAddRow = (row) => {
        const newRestoreData = restoreData.map((dataRow) =>
            dataRow?.item_id === row?.item_id ? {...dataRow, selected: true} : dataRow
        );
        setRestoreData(newRestoreData);
        const addedRow = newRestoreData.find(dataRow => dataRow?.item_id === row?.item_id);
        setAddedRows([...addedRows, addedRow]);
    };

    const mapAddedRows = (_restoreData) => {
        const addedRowIds = new Set(addedRows.map(row => row?.item_id));
        const newRestoreData = _restoreData.map((dataRow) =>
            addedRowIds.has(dataRow?.item_id) ? {...dataRow, selected: true} : dataRow
        );
        setRestoreData(newRestoreData);
    };

    const handleRemoveRow = (row) => {
        const newRestoreData = restoreData.map((dataRow) =>
            dataRow?.item_id === row?.item_id ? {...dataRow, selected: false} : dataRow
        );
        setRestoreData(newRestoreData);
        setAddedRows(addedRows.filter((oldDataRow) => oldDataRow?.item_id !== row?.item_id));
    };

    const clearAddedRows = () => {
        const newRestoreData = restoreData.map((row) => ({...row, selected: false}));
        setRestoreData(newRestoreData);
        setAddedRows([]);
    };

    // ─── Tab switch ──────────────────────────────────────────────────────────────
    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        clearAddedRows();
        setDisplayMode("All");
        // Destruction is tape-only
        if (newTab === "destruction" && listFilter === "itemList") {
            setListFilter("tapeList");
        }
    };

    // ─── Pagination ──────────────────────────────────────────────────────────────
    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
        if (listFilter === 'tapeList') getTapeData(newPage, searchQuery);
        else getFileData(newPage, searchQuery, filterData);
    };

    // ─── Display mode ────────────────────────────────────────────────────────────
    const handleDisplayModeChange = (event, mode) => {
        if (mode !== null) setDisplayMode(mode);
    };

    // ─── Filter popup ────────────────────────────────────────────────────────────
    const toggleFilterPopup = () => setFilterPopupOpen(!filterPopupOpen);
    const handleCloseFilterPopup = () => setFilterPopupOpen(false);
    useEffect(() => {
        if (listFilter === 'tapeList') handleCloseFilterPopup();
    }, [listFilter]);

    // ─── Search ──────────────────────────────────────────────────────────────────
    const clearSearch = () => {
        if (searchQuery) setSearchQuery("");
        setValue("searchBar", "");
    };

    // ─── List filter ─────────────────────────────────────────────────────────────
    const listFilterChange = (e) => {
        setListFilter(e.target.value);
        clearSearch();
        clearAddedRows();
    };

    // ─── Tape details ────────────────────────────────────────────────────────────
    const selectDetail = (row) => {
        setSelectedData(row?.item_id);
        setOpenTapeDetails(true);
    };

    // ─── Files type selector ─────────────────────────────────────────────────────
    const handleFilesTypeSelectorClick = (event) => setFilesTypeAnchorEl(event.currentTarget);
    const handleFilesTypeMenuClose = () => setFilesTypeAnchorEl(null);
    const handleFileTypeSelection = (newFilesType) => {
        handleFilesTypeMenuClose();
        setFilesType(newFilesType);
        setColumnsConfig(FILES_RESTORE_TABLE_COLUMNS[newFilesType]);
    };

    const filesTypeSelector = () => (
        <ToggleButtonGroup
            color="primary"
            sx={{"& .MuiToggleButtonGroup-grouped": {margin: 0.5, border: 0, "&.Mui-selected": {color: "#4E5152"}}}}
            exclusive
            aria-label="Selector for files type"
            value={null}
        >
            <ToggleButton className="display-mode" variant="outlined" value={filesType} onClick={handleFilesTypeSelectorClick}>
                {filesType}
                <ArrowDropDownIcon sx={{marginLeft: 1}}/>
            </ToggleButton>
            <Menu anchorEl={filesTypeAnchorEl} open={Boolean(filesTypeAnchorEl)} onClose={handleFilesTypeMenuClose}>
                <MenuItem onClick={() => handleFileTypeSelection("Seismic SegD view")}>
                    <Typography sx={{fontSize: 12}} component="span">Seismic SegD view</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleFileTypeSelection("Seismic SegY view")}>
                    <Typography sx={{fontSize: 12}} component="span">Seismic SegY view</Typography>
                </MenuItem>
                <Divider sx={{border: "0.5px groove #C7CCD0"}}/>
                <MenuItem onClick={() => handleFileTypeSelection("Standard files view")}>
                    <Typography sx={{fontSize: 12}} component="span">Standard files view</Typography>
                </MenuItem>
            </Menu>
        </ToggleButtonGroup>
    );

    // ─── Action column cell ──────────────────────────────────────────────────────
    const dynamicAddRemoveButtonCell = (row) => {
        if (row.selected) return (
            <StyledButtonOutlined
                variant="outlined"
                onClick={() => handleRemoveRow(row)}
                disabled={isReadOnly}
                sx={{background: "#FFFFFF", color: "#DD2A19"}}
            >
                Remove
            </StyledButtonOutlined>
        );

        return (
            <StyledButtonOutlined
                variant="outlined"
                onClick={() => handleAddRow(row)}
                disabled={isReadOnly}
                sx={activeTab === "destruction" ? {borderColor: "#FF6C65", color: "#FF6C65"} : {}}
            >
                {activeTab === "destruction" ? "Select" : "Add"}
            </StyledButtonOutlined>
        );
    };

    // ─── Checkout flow ───────────────────────────────────────────────────────────
    const handleOpenCheckout = () => setOptionsDialogOpen(true);

    const handleOptionsContinue = (values) => {
        // Strip falsy values and empty groups from the nested dict
        const cleaned = {};
        Object.entries(values).forEach(([group, options]) => {
            if (typeof options === "object" && options !== null) {
                const truthyOptions = Object.fromEntries(Object.entries(options).filter(([_, v]) => v));
                if (Object.keys(truthyOptions).length > 0) cleaned[group] = truthyOptions;
            } else if (options) {
                cleaned[group] = options;
            }
        });
        setActionPreferences(cleaned);
        setOptionsDialogOpen(false);
        setSummaryDialogOpen(true);
    };

    const handleSummaryBack = () => {
        setSummaryDialogOpen(false);
        setOptionsDialogOpen(true);
    };

    const handleSummaryContinue = () => {
        setSummaryDialogOpen(false);
        setContactFormOpen(true);
    };

    const handleContactFormClose = () => {
        setContactFormOpen(false);
        setSummaryDialogOpen(true);
    };

    const handleSuccessfulSubmit = () => {
        setContactFormOpen(false);
        setSummaryDialogOpen(false);
        setOptionsDialogOpen(false);
        setActionPreferences({});
        clearAddedRows();
    };

    // ─── Config per active tab ───────────────────────────────────────────────────
    const currentOptionsConfig = activeTab === "destruction" ? destructionOptionsConfig : restoreOptionsConfig;
    const currentCanProceed = activeTab === "destruction" ? destructionCanProceed : restoreCanProceed;
    const currentSummaryColumns = listFilter === "itemList" ? FILE_SUMMARY_COLUMNS : TAPE_SUMMARY_COLUMNS;
    const currentSummaryTitle = activeTab === "destruction" ? "Confirm Destruction" : "Checkout";

    // ─── Render ──────────────────────────────────────────────────────────────────
    return (
        <>
            <AppSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={closeSnackbar}
            />
            <ThemeProvider theme={dataManagementTheme}>
                {/* ─── Dialogs ─────────────────────────────────────────────── */}
                <OptionsDialog
                    open={optionsDialogOpen}
                    onClose={() => setOptionsDialogOpen(false)}
                    config={currentOptionsConfig}
                    onContinue={handleOptionsContinue}
                    onClear={() => {}}
                    computeCanProceed={currentCanProceed}
                />

                <CheckoutSummaryDialog
                    open={summaryDialogOpen}
                    onClose={() => setSummaryDialogOpen(false)}
                    title={currentSummaryTitle}
                    items={addedRows}
                    columns={currentSummaryColumns}
                    onRemove={handleRemoveRow}
                    onBack={handleSummaryBack}
                    onContinue={handleSummaryContinue}
                    continueDisabled={addedRows.length === 0}
                />

                {/* Hydration confirmation dialog */}
                <ConfirmDialog
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    onConfirm={handleHydrationRequested}
                    title="Confirm Hydration Request"
                    message={`You are requesting file-level data hydration. This hydration will expire on ${selectedDisplayLocal}.`}
                />

                <ContactForm
                    isFormOpen={contactFormOpen}
                    closeForm={handleContactFormClose}
                    actionRelatedData={{
                        addedRows,
                        dialogInitiator: listFilter,
                        actionPreferences,
                    }}
                    onSuccessfulSubmit={handleSuccessfulSubmit}
                    contactType={activeTab}
                    openSnackbar={openSnackbar}
                />

                {/* ─── View content ───────────────────────────────────────── */}
                <Box sx={{paddingTop: 4, paddingX: {xs: 0.5, md: 4}, mb: bottomDrawerHeight}}>
                    <TabbedWrapper
                        tabs={tabsConfig}
                        defaultTab="restore"
                        onTabChange={handleTabChange}
                        bordered
                    >
                        {/* Toggle groups */}
                        <Box sx={{display: "flex", flexDirection: "column", gap: 1.5, alignItems: "flex-start"}}>
                            <Paper
                                elevation={0}
                                sx={{border: "1px solid #FFFFFF", display: "flex", backgroundColor: PrimaryColor, borderRadius: "8px"}}
                            >
                                <ToggleButtonGroup
                                    color="primary"
                                    sx={{"& .MuiToggleButtonGroup-grouped": {margin: 0.5, border: 0, "&.Mui-selected": {color: "#3E3A3F"}}}}
                                    exclusive
                                    value={listFilter}
                                    onChange={(e) => listFilterChange(e)}
                                >
                                    <ToggleButton className="display-mode" value="tapeList">Show Tapes</ToggleButton>
                                    {/*
                                      * Files focus: "Show Files", on file-level deployments only, so one code base serves
                                      * AWS file-level customers and deployments without file-level data (Azure), where it
                                      * used to be a permanently greyed-out button. Greyed while the data is not hydrated.
                                      * See docs/file-level-ui-removal.md.
                                      */}
                                    {activeTab === "restore" && hydrationInfo.isFileLevel && (
                                        <ToggleButton className="display-mode" value="itemList" disabled={!hasFiles} sx={{...(hasFiles ? {} : {opacity: 0.5, pointerEvents: 'none'})}}>
                                            Show Files
                                        </ToggleButton>
                                    )}
                                </ToggleButtonGroup>
                                {listFilter === "itemList" && filesTypeSelector()}
                            </Paper>

                            {/* ─── Hydration hint + controls (Restore tab, file-level only) ─── */}
                            {activeTab === "restore" && hydrationInfo.isFileLevel && (
                            <Box sx={{width: "100%"}}>
                                {hydrationInfo.state === 'hydrated' && (
                                    <HintText>
                                        * File-level data available until {displayInLocalTz(hydrationInfo.expires)}.
                                        {hydrationPollTarget && <>{" "}<CountdownDisplay targetUTC={hydrationPollTarget} onExpired={() => refreshHydrationStatusRef.current?.()}/> remaining.</>}
                                    </HintText>
                                )}
                                {hydrationInfo.state === 'hydrating' && (
                                    <HintText>
                                        * Hydration in progress since {displayInLocalTz(hydrationInfo.since)}.{" "}
                                        {hydrationPollTarget && <>Next check in <CountdownDisplay targetUTC={hydrationPollTarget} onExpired={() => { refreshHydrationStatusRef.current?.(); setHydrationPollTarget(moment().add(POLL_INTERVAL_SECONDS, 'seconds').toISOString()); }}/>.</>}
                                    </HintText>
                                )}
                                {hydrationInfo.state === 'dehydrating' && (
                                    <HintText>* File-level data is dehydrating. Await completion before requesting hydration again.</HintText>
                                )}
                                {hydrationInfo.state === 'dehydrated' && (
                                    <HintText>* File-level data unavailable. Request hydration below.</HintText>
                                )}
                                {hydrationInfo.state === 'dehydrated' && (
                                    <Box sx={{mt: 1, display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-end"}}>
                                        <ToggleButtonGroupField
                                            label="Hydrated file level data expires in"
                                            items={HYDRATION_PRESETS.map(({label}) => ({label, value: label}))}
                                            value={selectedPreset}
                                            onChange={(val) => {
                                                setSelectedPreset(val);
                                                const preset = HYDRATION_PRESETS.find(p => p.label === val);
                                                if (preset?.days) {
                                                    // Fixed preset — auto-fill picker (will be disabled)
                                                    setCustomDate(moment.tz(PERTH_TZ).add(preset.days, 'days').format("YYYY-MM-DDTHH:mm"));
                                                } else {
                                                    // Custom — keep existing value, default to now + 2 days if empty
                                                    if (!customDate) setCustomDate(moment.tz(PERTH_TZ).add(2, 'days').format("YYYY-MM-DDTHH:mm"));
                                                }
                                            }}
                                        />
                                        <DateTimePickerField
                                            value={customDate}
                                            onChange={setCustomDate}
                                            minDate={minLocalDate}
                                            disabled={selectedPreset !== "Custom"}
                                            inputHeight={58}
                                        />
                                        <Box sx={{display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "center"}}>
                                            <StyledButtonContained
                                                variant="contained"
                                                disabled={!canRequestHydration || isReadOnly}
                                                onClick={() => setConfirmOpen(true)}
                                                sx={{height: 40, px: 2, fontSize: 12}}
                                            >
                                                Request Hydration
                                            </StyledButtonContained>
                                            {selectedDisplayLocal && (
                                                <HintText>Expires: {selectedDisplayLocal}</HintText>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                            )}

                            <Paper
                                elevation={0}
                                sx={{border: "1px solid #FFFFFF", display: "flex", backgroundColor: PrimaryColor, borderRadius: "8px"}}
                            >
                                <ToggleButtonGroup
                                    color="primary"
                                    sx={{"& .MuiToggleButtonGroup-grouped": {margin: 0.5, border: 0, "&.Mui-selected": {color: "#3E3A3F"}}}}
                                    value={displayMode}
                                    exclusive
                                    onChange={handleDisplayModeChange}
                                >
                                    <ToggleButton className="display-mode" value="All">Show All</ToggleButton>
                                    <ToggleButton className="display-mode" value="Selected">
                                        {addedRows.length !== 0 ? addedRows.length : ""} Selected Only
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Paper>
                        </Box>

                        {/* Search bar */}
                        <Box sx={{display: "flex", gap: 1.5, alignItems: "center", pt: 1.5, maxWidth: "600px"}}>
                            <form id="SearchBarForm" onSubmit={handleSubmit(submitSearchFn, errs => console.error(errs))} style={{display: "flex", gap: 12, alignItems: "center"}}>
                                <Controller
                                    name="searchBar"
                                    control={control}
                                    defaultValue=""
                                    render={({field}) => (
                                        <InputBase
                                            {...field}
                                            className={classes.selectLabel}
                                            type="text"
                                            placeholder="Search"
                                            error={Boolean(errors["searchBar"])}
                                            fullWidth
                                            sx={{
                                                fontSize: "16px", fontWeight: "400",
                                                height: "50px", backgroundColor: PrimaryColor,
                                                borderRadius: "8px", px: "20px", border: "1px solid #C7CCD0",
                                                color: "#FFFFFF",
                                            }}
                                            startAdornment={
                                                <InputAdornment position="start" sx={{color: "#A6AEC1"}}>
                                                    <SearchIcon/>
                                                </InputAdornment>
                                            }
                                            endAdornment={
                                                <InputAdornment position="end" sx={{color: "#A6AEC1"}}>
                                                    <IconButton onClick={() => clearSearch()}>
                                                        <Cancel sx={{color: "#FFFFFF"}}/>
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                        />
                                    )}
                                />
                                <StyledButtonOutlined variant="outlined" type="submit">Search</StyledButtonOutlined>
                                {listFilter === 'itemList' && activeTab === "restore" && (
                                    <StyledButtonOutlined
                                        variant="outlined"
                                        onClick={toggleFilterPopup}
                                        sx={{
                                            width: 40, height: 40, padding: 0, minWidth: 40,
                                            ...(filterData && Object.keys(filterData).length > 0 ? {backgroundColor: '#FFC540', color: '#3A3E3F', '& svg': {color: '#3A3E3F'}} : {})
                                        }}
                                    >
                                        <FilterAltOutlinedIcon fontSize="small"/>
                                    </StyledButtonOutlined>
                                )}
                            </form>
                            {listFilter === 'itemList' && activeTab === "restore" && (
                                <Filters
                                    filtersStructure={restoreFiltersStructure}
                                    variant={'fileFilters'}
                                    filterDialogCloseFn={handleCloseFilterPopup}
                                    isDialogOpen={filterPopupOpen}
                                    setFilterDataFn={setFilterData}
                                />
                            )}
                        </Box>

                        {/*
                          * What the list is showing, and the way back to everything.
                          *
                          * A filtered list that does not say it is filtered is a list that lies: the reader
                          * clicked a number on the dashboard, arrived on a page headed by the same toggles as
                          * always, and has no way to tell why their six thousand tapes are now eleven.
                          * Clearable in one click, because it was reached in one click.
                          */}
                        {mediaFilter && (
                            <Box sx={{mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                                <Chip
                                    icon={<FilterAltIcon/>}
                                    label={mediaFilterChipText(mediaFilter)}
                                    onDelete={clearMediaFilter}
                                    /*
                                     * Named, unlike MUI's bare dismiss cross. There is a second cross on this
                                     * page — the one that empties the search box — and an unlabelled icon
                                     * leaves a screen reader unable to say which of the two it is on.
                                     */
                                    deleteIcon={<Cancel titleAccess="Clear the media filter"/>}
                                    variant="outlined"
                                    /*
                                     * Colours spelled out because MUI's default chip palette is the
                                     * light-theme one: left alone, the funnel and the dismiss cross render
                                     * near-black on this page and the only readable part is the label.
                                     */
                                    sx={{
                                        fontFamily: 'Poppins',
                                        fontSize: '12px',
                                        maxWidth: '100%',
                                        color: 'rgba(255, 255, 255, 0.85)',
                                        borderColor: 'rgba(255, 255, 255, 0.24)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                                        '& .MuiChip-icon': {color: 'rgba(255, 255, 255, 0.60)'},
                                        '& .MuiChip-deleteIcon': {
                                            color: 'rgba(255, 255, 255, 0.60)',
                                            '&:hover': {color: '#FFFFFF'},
                                        },
                                    }}
                                />
                            </Box>
                        )}

                        {/*
                          * Data table — a placeholder while there is no scope to fetch with: the first
                          * render's pause, and an admin at 'All Jobs' whose job list has not landed. The
                          * empty table is left to speak for a scope that really holds nothing.
                          */}
                        <Box sx={{mt: 2}}>
                            {isInitialLoad || waitingForJobList ? (
                                <TablePlaceholder />
                            ) : (
                                <DataTable
                                    data={restoreData.length > 0 ? (displayMode === "All" ? restoreData : addedRows) : []}
                                    columnsConfig={addCustomRenderer(addClickCallback(columnsConfig, {"Tape Type": selectDetail}), {"Action": dynamicAddRemoveButtonCell})}
                                    dataContainsFormatting={String(listFilter) === "itemList"}
                                    searchQuery={searchQuery}
                                    showPagination={String(displayMode) !== 'Selected'}
                                    entriesCount={entriesCount}
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    handlePageChangeCallback={handlePageChange}
                                />
                            )}
                        </Box>
                    </TabbedWrapper>
                </Box>

                {/* Bottom Drawer — items selected */}
                <Drawer
                    variant="persistent"
                    anchor="bottom"
                    open={addedRows.length > 0}
                    sx={{
                        "& .MuiDrawer-paper": {
                            backgroundColor: "#3A3E3F",
                            boxShadow: "0px -7px 11px 0px #00000029",
                            height: bottomDrawerHeight,
                        },
                    }}
                >
                    <Box gap={4} display="flex" justifyContent="right" margin={1}>
                        <Typography sx={{textAlign: "left", fontSize: 18, color: "#FFFFFF", fontWeight: 400, mt: 2}}>
                            {addedRows.length} {activeTab === "destruction" ? "tape(s)" : "file(s)"} selected
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={clearAddedRows}
                            sx={{
                                width: "220px", height: "60px", borderRadius: "30px", color: "#FFFFFF",
                                textTransform: "none", border: "1px solid #FFFFFF",
                                "&:hover": {backgroundColor: "#C7CCD0"},
                            }}
                        >
                            Clear
                        </Button>
                        <StyledButtonContained
                            variant="contained"
                            onClick={handleOpenCheckout}
                            disabled={isReadOnly}
                            sx={{
                                width: "220px", height: "60px",
                                ...(activeTab === "destruction" ? {
                                    background: "#DD2A19",
                                    color: "#FFFFFF",
                                    "&:hover": {background: "#BB2215"},
                                } : {}),
                            }}
                        >
                            {activeTab === "destruction" ? "Request Destruction" : "Checkout"}
                        </StyledButtonContained>
                    </Box>
                </Drawer>
            </ThemeProvider>
            <TapeDetails open={openTapeDetails} closePopup={() => setOpenTapeDetails(false)} tapeId={selectedData}/>
        </>
    );
};
